import * as React from 'react'
import { Collapse } from 'react-bootstrap'
import { DateTime } from 'luxon'

// components
import BodyFrame, { FrameType } from '../../components/subsync/frame'
import ModalList from '../../components/ui/modalList/modalList'
import SubSyncOnboardModal from '../../components/subsync/onboardModal'
import * as Messages from '../../components/ui/messages/messages'

// definitions
import {
	AccountStripeOrg,
	AccountXeroOrg,
	StripeInvoice,
	StripeOrgAccountXeroOrg,
	StripeProduct,
	StripeTaxRate,
	XeroAccount,
	XeroItem,
	XeroTaxRate,
	XeroContact,
	StripeMappingType,
	XeroTrackingCategory,
	StripeProductXeroMapping,
	TermsVersion,
} from '../../../../back-end/utilities/apiDefinitions'
import { ModalState } from '../leavecal/leavecal.d'
import { DetailedStripeOrgAccountXeroOrg } from '../../components/subsync/settings/settings'
import { StripePayout } from '../../../../back-end/common/stripePayout'
import { StripeCharge } from '../../../../back-end/common/stripeCharge'

// images
import EventsImage from '../../images/subSyncIcons/Pages.svg'
import SettingsImage from '../../images/subSyncIcons/Settings.svg'
import StripeLogo from '../../images/stripe-logo.svg'

// utilities
import * as Request from '../../utilities/request'
import * as I from '../../utilities/me'
import { AppContext } from '../../App'
import TermsAndConditionsModal from '../../components/common/termsAndConditionsModal'
import { Link, useLocation } from 'react-router-dom'

const getFrameFromHash = (hash: string): FrameType => {
	return hash === 'overview' || hash.includes('settings') ? (hash as FrameType) : 'overview'
}

interface DetailedStripeCharge extends StripeCharge {
	stripeOrg?: AccountStripeOrg
	xeroOrg?: AccountXeroOrg
}

interface DetailedStripePayout extends StripePayout {
	stripeOrg?: AccountStripeOrg
	xeroOrg?: AccountXeroOrg
}

interface HistoryView {
	events: StripeInvoice[]
	payouts: DetailedStripePayout[]
	payments: DetailedStripeCharge[]
}

interface AccountStripeOrgWithData extends AccountStripeOrg {
	products: StripeProduct[]
	taxRates: StripeTaxRate[]
}

interface AccountXeroOrgWithData extends AccountXeroOrg {
	accounts: XeroAccount[]
	items: XeroItem[]
	taxRates: XeroTaxRate[]
	contacts: XeroContact[]
	trackingCategories: XeroTrackingCategory[]
}

interface OrgSettings {
	stripeOrgs: AccountStripeOrgWithData[]
	xeroOrgs: AccountXeroOrgWithData[]
	orgMappings: DetailedStripeOrgAccountXeroOrg[]
	stripeMappingTypes: StripeMappingType[]
}

interface InvoiceRetryBody {
	stripeInvoiceID: string
	stripeOrgID?: string
	tenantID?: string
	xeroOrgID?: string
	userAccountXeroOrgAuthID?: string
}

interface EventRetryBody {
	stripeEventID: string
	stripeOrgID: string
}

const initialModalState: ModalState = {
	modal: '',
	props: {},
	size: 'sm',
}

const SubSync = () => {
	const { appState } = React.useContext(AppContext)
	const location = useLocation()

	const [messages, updateMessages] = Messages.useMessageReducer([])

	const [currentFrame, updateCurrentFrame] = React.useState<FrameType>(getFrameFromHash(location.hash.substring(1)))
	const [pageStatus, updatePageStatus] = React.useState<string>('Loading')
	const [shownModal, updateShownModal] = React.useState<ModalState>(initialModalState)
	const [showSubMenu, updateShowSubMenu] = React.useState<boolean>(getFrameFromHash(location.hash.substring(1)).includes('settings'))

	const [historyView, updateHistoryView] = React.useState<HistoryView>({
		events: [],
		payouts: [],
		payments: [],
	})
	const [orgSettings, updateOrgSettings] = React.useState<OrgSettings>({
		stripeOrgs: [],
		xeroOrgs: [],
		orgMappings: [],
		stripeMappingTypes: [],
	})

	const refreshData = React.useCallback(async () => {
		const fetchData = async () => {
			updatePageStatus('Loading')
			const eventCutoff = DateTime.local().minus({ months: 3 }).startOf('month').toUTC().toJSON()
			let invoiceData: StripeInvoice[] = []
			let payoutData: StripePayout[] = []
			let paymentData: StripeCharge[] = []
			const [
				stripeOrgs,
				xeroOrgs,
				orgMappings,
				products,
				accounts,
				items,
				stripeTaxRates,
				xeroTaxRates,
				contacts,
				trackingCategories,
				stripeMappingTypes,
				users,
				currentAccount,
				termsVersions,
				xeroUrl,
			] = await Promise.all(
				[
					Request.get('accountstripeorg?where=StripeOrgIsConnected==1', appState.authState),
					Request.get('organisation?where=AccountXeroOrgIsSubSync==1', appState.authState),
					Request.get('stripeorgaccountxeroorg', appState.authState),
					Request.get('stripeproduct', appState.authState),
					Request.get('xeroaccount', appState.authState),
					Request.get('xeroitem', appState.authState),
					Request.get('stripetaxrate', appState.authState),
					Request.get('xerotaxrate', appState.authState),
					Request.get('xeroContact', appState.authState),
					Request.get('xerotrackingcategory', appState.authState),
					Request.get('stripemappingtype', appState.authState),
					Request.get('user', appState.authState),
					Request.get('account', appState.authState),
					Request.get('termsversion?where=TermsVersionIsSubSync==1', appState.authState),
				]
					.concat(I.have('admin', appState.permission[appState.context]) ? [Request.get('xero/url/subsync', appState.authState)] : [])
					.concat([
						Request.getPagedData(
							`stripeinvoice?where=sse.CreateTs>=${eventCutoff}&`,
							4000,
							(data) => (invoiceData = invoiceData.concat(data.stripeInvoices)),
							appState.authState
						),
						Request.getPagedData(
							`stripepayout?where=setr.CreateTs>=${eventCutoff}&`,
							4000,
							(data) => (payoutData = payoutData.concat(data.stripePayouts)),
							appState.authState
						),
						Request.getPagedData(
							`stripecharge?where=setr.CreateTs>=${eventCutoff}&`,
							4000,
							(data) => (paymentData = paymentData.concat(data.stripeCharges)),
							appState.authState
						),
					])
			)

			// First do the Terms and Conditions Checks
			const currentTermsVersion = termsVersions.data.termsVersions.reduce((a: TermsVersion, b: TermsVersion) =>
				Math.abs(new Date(a.effectiveDate).getTime() - Date.now()) < Math.abs(new Date(b.effectiveDate).getTime() - Date.now()) ? a : b
			)
			if (I.am(appState.permission[appState.context]) === 'Admin') {
				const currentAccountObj = currentAccount.data.accounts.find(
					(a: { accountID: string }) => a.accountID === appState!.authState!.attributes!['custom:currentAccountId']
				)

				if (currentAccountObj.termsAcceptance && currentAccountObj.termsAcceptance.length > 0) {
					const latestAcceptance = currentAccountObj.termsAcceptance.find(
						(ta: { termsVersionID: string }) => ta.termsVersionID === currentTermsVersion.termsVersionID
					)
					if (latestAcceptance) {
						if (latestAcceptance.termsAcceptanceStatus === 'denied') {
							//someone has denied them so show message
							updateShownModal({
								modal: 'TermsAndConditionsAcceptance',
								props: {
									type: 'DeniedAlert',
									product: 'SubSync',
									appState: appState,
									latestTermsVersion: currentTermsVersion,
									denialRecord: latestAcceptance,
									denialUser: users.data.users.find((u: { userID: string }) => u.userID === latestAcceptance.userID),
									updateShownModal: updateShownModal,
								},
								size: 'md',
							})
						}
					} else {
						// they have an old version
						updateShownModal({
							modal: 'TermsAndConditionsAcceptance',
							props: {
								type: 'ChooseOption',
								product: 'SubSync',
								appState: appState,
								latestTermsVersion: currentTermsVersion,
								updateShownModal: updateShownModal,
							},
							size: 'md',
						})
					}
				} else {
					// never accepted so promp them too
					updateShownModal({
						modal: 'TermsAndConditionsAcceptance',
						props: {
							type: 'ChooseOption',
							product: 'SubSync',
							appState: appState,
							latestTermsVersion: currentTermsVersion,
							updateShownModal: updateShownModal,
						},
						size: 'md',
					})
				}
			}

			// update onboarding
			if (
				(xeroOrgs.data.accountXeroOrgs.length === 0 || stripeOrgs.data.accountStripeOrgs.length === 0) &&
				I.am(appState.permission[appState.context]) === 'Admin'
			) {
				updateShownModal({
					modal: 'Connect',
					props: {
						xeroConsentUrl: xeroUrl.data.url,
						xeroIsConnected: xeroOrgs.data.accountXeroOrgs.length > 0,
						stripeIsConnected: stripeOrgs.data.accountStripeOrgs.length > 0,
					},
					size: 'md',
				})
			}

			updateHistoryView({
				...historyView,
				events: combinePagedInvoiceData(invoiceData),
				payouts: combinePagedStripeEventData(payoutData, stripeOrgs.data.accountStripeOrgs, xeroOrgs.data.accountXeroOrgs),
				payments: combinePagedStripeEventData(paymentData, stripeOrgs.data.accountStripeOrgs, xeroOrgs.data.accountXeroOrgs),
			})
			updateOrgSettings({
				stripeOrgs: groupStripeOrgsWithData(stripeOrgs.data.accountStripeOrgs, products.data.stripeProducts, stripeTaxRates.data.stripeTaxRates),
				xeroOrgs: groupXeroOrgsWithData(
					xeroOrgs.data.accountXeroOrgs,
					accounts.data.xeroAccounts,
					items.data.xeroItems,
					xeroTaxRates.data.xeroTaxRates,
					contacts.data.xeroContacts,
					trackingCategories.data.xeroTrackingCategories
				),
				orgMappings: formatOrgMappings(orgMappings.data.stripeOrgAccountXeroOrgs, products.data.stripeProducts),
				stripeMappingTypes: stripeMappingTypes.data.stripeMappingTypes,
			})
			updatePageStatus('Finished')
		}

		if (appState.authState.isLoggedIn && appState.permission[appState.context]) {
			fetchData()
		}
	}, [appState.authState, appState.permission[appState.context]]) // eslint-disable-line

	React.useEffect(() => {
		if (appState.authState.isLoggedIn) {
			refreshData()
		}
	}, [appState.authState, refreshData])

	const refreshSettingsData = async () => {
		updatePageStatus('Loading')
		// First we trigger getting the info from xero for all the Orgs
		await Promise.all(
			orgSettings.xeroOrgs.map(async (org) => {
				const body = {
					tenantID: org.xeroOrg!.tenantID,
					xeroOrgID: org.xeroOrg!.xeroOrgID,
					version: org.xeroOrg!.version,
					userAccountXeroOrgAuthID: org.userAccountXeroOrgAuth!.id,
				}
				await Request.put('stripexerodataupdate', body, appState.authState)
			})
		)
		// then we get everything saved in our DB after the refresh
		const [xeroOrgs, accounts, items, xeroTaxRates, stripeOrgs, products, stripeTaxRates, contacts, trackingCategories] = await Promise.all([
			Request.get('organisation?where=AccountXeroOrgIsSubSync==1', appState.authState),
			Request.get('xeroaccount', appState.authState),
			Request.get('xeroitem', appState.authState),
			Request.get('xerotaxrate', appState.authState),
			Request.get('accountstripeorg', appState.authState),
			Request.get('stripeproduct', appState.authState),
			Request.get('stripetaxrate', appState.authState),
			Request.get('xerocontact', appState.authState),
			Request.get('xerotrackingcategory', appState.authState),
		])
		updateOrgSettings({
			stripeOrgs: groupStripeOrgsWithData(stripeOrgs.data.accountStripeOrgs, products.data.stripeProducts, stripeTaxRates.data.stripeTaxRates),
			xeroOrgs: groupXeroOrgsWithData(
				xeroOrgs.data.accountXeroOrgs,
				accounts.data.xeroAccounts,
				items.data.xeroItems,
				xeroTaxRates.data.xeroTaxRates,
				contacts.data.xeroContacts,
				trackingCategories.data.xeroTrackingCategories
			),
			orgMappings: formatOrgMappings(orgSettings.orgMappings, products.data.stripeProducts),
			stripeMappingTypes: orgSettings.stripeMappingTypes,
		})
		updatePageStatus('Finished')
	}

	const updateFrameAndHash = (frame: FrameType) => {
		window.location.hash = frame
		updateCurrentFrame(frame)
	}

	const refreshInvoiceData = async () => {
		updatePageStatus('Loading')
		const eventCutoff = DateTime.local().minus({ months: 3 }).startOf('month').toUTC().toJSON()
		let invoiceData: StripeInvoice[] = []
		await Request.getPagedData(
			`stripeinvoice?where=sse.CreateTs>=${eventCutoff}&`,
			4000,
			(data) => (invoiceData = invoiceData.concat(data.stripeInvoices)),
			appState.authState
		)

		updateHistoryView({
			...historyView,
			events: combinePagedInvoiceData(invoiceData),
		})
		updatePageStatus('Finished')
	}

	const refreshPayoutData = async () => {
		updatePageStatus('Loading')
		const eventCutoff = DateTime.local().minus({ months: 3 }).startOf('month').toUTC().toJSON()
		let payoutData: StripePayout[] = []
		await Request.getPagedData(
			`stripepayout?where=setr.CreateTs>=${eventCutoff}&`,
			4000,
			(data) => (payoutData = payoutData.concat(data.stripePayouts)),
			appState.authState
		)
		updateHistoryView({
			...historyView,
			payouts: combinePagedStripeEventData(payoutData, orgSettings.stripeOrgs, orgSettings.xeroOrgs),
		})
		updatePageStatus('Finished')
	}

	const refreshPaymentData = async () => {
		updatePageStatus('Loading')
		const eventCutoff = DateTime.local().minus({ months: 3 }).startOf('month').toUTC().toJSON()
		let paymentData: StripeCharge[] = []
		await Request.getPagedData(
			`stripecharge?where=setr.CreateTs>=${eventCutoff}&`,
			4000,
			(data) => (paymentData = paymentData.concat(data.stripeCharges)),
			appState.authState
		)
		updateHistoryView({
			...historyView,
			payments: combinePagedStripeEventData(paymentData, orgSettings.stripeOrgs, orgSettings.xeroOrgs),
		})
		updatePageStatus('Finished')
	}

	const retryFailedInvoiceTransaction = async (body: InvoiceRetryBody) => {
		const org = orgSettings.xeroOrgs.find((o) => o.xeroOrg!.xeroOrgID === body.xeroOrgID)
		if (org && org.userAccountXeroOrgAuth && org.xeroOrg) {
			await Request.put(
				`striperetryinvoicetransaction`,
				{ ...body, userAccountXeroOrgAuthID: org.userAccountXeroOrgAuth.id, tenantID: org.xeroOrg.tenantID },
				appState.authState
			)
		}
		updateMessages({
			type: 'add',
			data: {
				severity: 'success',
				message: 'Retrying transaction - this might take a minute',
				timeout: 3000,
				dismissible: true,
			},
		})
	}

	const retryFailedEvent = async (body: EventRetryBody) => {
		await Request.put(`striperetryevent`, body, appState.authState)
		updateMessages({
			type: 'add',
			data: {
				severity: 'success',
				message: 'Retrying transaction - this might take a minute',
				timeout: 3000,
				dismissible: true,
			},
		})
	}

	const isTrialEnded =
		appState.subscription &&
		appState.subscription.isTrial &&
		appState.subscription.currentPeriodEnd &&
		DateTime.fromISO(appState.subscription.currentPeriodEnd).diffNow().toMillis() < 0 &&
		appState.subscription.cancelledAt &&
		DateTime.fromISO(appState.subscription.cancelledAt).diffNow().toMillis() > 0
	const isValid = appState.subscription && appState.subscription.valid

	return (
		<div className="leavecal-root">
			<div className="leavecal-container">
				<div className="leavecal-blue-bar"></div>
				{isTrialEnded && orgSettings.xeroOrgs.length > 0 && (
					<div className="alert alert-danger mb-0" role="alert">
						Your trial has ended, your account will be shutdown on{' '}
						{DateTime.fromISO(appState.subscription!.cancelledAt!).toLocaleString(DateTime.DATETIME_MED)} unless you{' '}
						<Link to="/billing">select a plan.</Link>
					</div>
				)}
				{appState.subscription && !isValid && !isTrialEnded && orgSettings.xeroOrgs.length > 0 && (
					<div className="alert alert-danger mb-0" role="alert">
						Your subscription is not active. Please{' '}
						<a href="#billing" onClick={() => updateFrameAndHash('billing')}>
							update your payment details
						</a>{' '}
						to continue your service.
					</div>
				)}
			</div>

			<ModalList
				handleClose={() => {
					updateShownModal(initialModalState)
				}}
				show={shownModal.modal}
				data={shownModal.props}
				size={shownModal.size}
				style={
					shownModal.props && shownModal.props.type && shownModal.props.type === 'DeniedAlert' ? { keyboard: true, backdrop: 'static' } : undefined
				}
				modals={[
					{
						id: 'Connect',
						body: SubSyncOnboardModal,
					},
					{
						id: 'TermsAndConditionsAcceptance',
						body: TermsAndConditionsModal,
					},
				]}
			/>

			<div className="subsync-body">
				<div className="subsync-left-menu">
					<div>
						{I.have('admin', appState.permission[appState.context]) ? (
							<div
								onClick={() => {
									updateFrameAndHash('overview')
									updateShowSubMenu(false)
								}}
								className={'subsync-menu-item' + (currentFrame === 'overview' ? ' active' : '')}
							>
								<img src={EventsImage} />
								<p>Events</p>
							</div>
						) : null}
						{I.have('admin', appState.permission[appState.context]) ? (
							<>
								<div
									onClick={() => {
										updateShowSubMenu(!showSubMenu || currentFrame.includes('settings'))
										const firstStripeOrg = orgSettings.stripeOrgs.at(0)
										if (firstStripeOrg) {
											updateFrameAndHash('settings_' + firstStripeOrg.accountStripeOrgID)
										} else {
											// Show message telling user to connect Stripe
											updateMessages({
												type: 'add',
												data: {
													severity: 'danger',
													message: 'You need to connect a Stripe account to access settings.',
													timeout: 5000,
													dismissible: true,
												},
											})
										}
									}}
									className={'subsync-menu-item'}
								>
									<img src={SettingsImage} />
									<p>Settings</p>
								</div>
								<Collapse in={showSubMenu}>
									<div className={'subsync-sub-menu-wrapper'}>
										{orgSettings.stripeOrgs.map((so) => (
											<div
												key={so.accountStripeOrgID}
												onClick={() => updateFrameAndHash('settings_' + so.accountStripeOrgID)}
												className={'subsync-menu-item sub-menu-item' + (currentFrame.includes(so.accountStripeOrgID!) ? ' active' : '')}
											>
												<img alt="stripe-logo" style={{ width: '30px', borderRadius: '25px' }} src={StripeLogo} />
												<p style={{ wordBreak: 'break-word' }}>{so.stripeOrg?.name || ''}</p>
											</div>
										))}
									</div>
								</Collapse>
							</>
						) : null}
						{I.have('admin', appState.permission[appState.context]) ? (
							<div
								onClick={() => {
									updateFrameAndHash('billing')
									updateShowSubMenu(false)
								}}
								className={'subsync-menu-item' + (currentFrame === 'billing' ? ' active' : '')}
							>
								<img src={EventsImage} />
								<p>Billing &amp; Subscriptions</p>
							</div>
						) : null}
						{/* this is to fill up the rest of the page */}
						<div style={{ flexGrow: 1 }}></div>
					</div>
				</div>
				<div className="subsync-main-content">
					<BodyFrame
						appState={appState}
						pageStatus={pageStatus}
						currentFrame={currentFrame}
						historyView={historyView}
						refreshSettingsData={refreshSettingsData}
						refreshInvoiceData={refreshInvoiceData}
						refreshPayoutData={refreshPayoutData}
						refreshPaymentData={refreshPaymentData}
						updateHistoryView={updateHistoryView}
						orgSettings={orgSettings}
						updateOrgSettings={updateOrgSettings}
						retryFailedInvoiceTransaction={retryFailedInvoiceTransaction}
						retryFailedEvent={retryFailedEvent}
					/>
				</div>
			</div>

			<Messages.Messages messages={messages} updateMessage={updateMessages} />
		</div>
	)
}

const combinePagedInvoiceData = (invoices: StripeInvoice[]) =>
	invoices.reduce((final, curr) => {
		const invoiceIndex = final.findIndex((prev) => prev.stripeInvoiceID === curr.stripeInvoiceID)

		if (invoiceIndex >= 0) {
			final[invoiceIndex].events = final[invoiceIndex].events!.concat(curr.events!)
		} else {
			final.push(curr)
		}

		return final
	}, [] as StripeInvoice[])

const combinePagedStripeEventData = (payouts: (StripePayout | StripeCharge)[], stripeOrgs: AccountStripeOrg[], xeroOrgs: AccountXeroOrg[]) => {
	const stripeOrgMap = Object.fromEntries(stripeOrgs.map((org) => [org.stripeOrg!.stripeOrgID, org]))
	const xeroOrgMap = Object.fromEntries(xeroOrgs.map((org) => [org.accountXeroOrgID, org]))

	const payoutData = payouts.reduce((final, curr) => {
		const payoutIndex = final.findIndex(
			(p) =>
				((p as DetailedStripePayout).stripePayoutID && (p as DetailedStripePayout).stripePayoutID === (curr as StripePayout).stripePayoutID) ||
				((p as DetailedStripeCharge).stripeChargeID && (p as DetailedStripeCharge).stripeChargeID === (curr as StripeCharge).stripeChargeID)
		)
		const eventIndex = payoutIndex >= 0 ? final[payoutIndex].events!.findIndex((e) => e.stripeEventID === curr.events![0].stripeEventID) : -1
		const transactionIndex =
			eventIndex >= 0
				? final[payoutIndex].events![eventIndex].transactions!.findIndex(
						(t) => t.stripeEventTransactionID === curr.events![0].transactions?.[0].stripeEventTransactionID
				  )
				: -1
		const errorIndex =
			transactionIndex >= 0
				? final[payoutIndex].events![eventIndex].transactions![transactionIndex].errors!.findIndex(
						(e) => e.stripeEventTransactionErrorID === curr.events![0].transactions![0]?.errors?.[0]?.stripeEventTransactionErrorID
				  )
				: -1

		if (payoutIndex < 0) {
			final.push({
				...curr,
				stripeOrg: stripeOrgMap[curr.stripeOrgID!],
				xeroOrg: xeroOrgMap[curr.events![0].transactions?.[0]?.accountXeroOrgID ?? ''],
			})
		} else if (eventIndex < 0) {
			final[payoutIndex].events! = final[payoutIndex].events!.concat(curr.events!)
		} else if (transactionIndex < 0) {
			final[payoutIndex].events![eventIndex].transactions! = final[payoutIndex].events![eventIndex].transactions!.concat(curr.events![0].transactions!)
		} else if (errorIndex < 0) {
			final[payoutIndex].events![eventIndex].transactions![transactionIndex].errors! = final[payoutIndex].events![eventIndex].transactions![
				transactionIndex
			].errors!.concat(curr.events![0].transactions![0].errors!)
		}

		return final
	}, [] as (DetailedStripePayout | DetailedStripeCharge)[])

	return payoutData
}

const groupStripeOrgsWithData = (orgs: AccountStripeOrg[], products: StripeProduct[], taxRates: StripeTaxRate[]) =>
	orgs.map((org) => ({
		...org,
		products: products.filter((product) => product.stripeOrgID === org.stripeOrg!.stripeOrgID),
		taxRates: taxRates.filter((taxRate) => taxRate.stripeOrgID === org.stripeOrg!.stripeOrgID),
	}))

const groupXeroOrgsWithData = (
	orgs: AccountXeroOrg[],
	accounts: XeroAccount[],
	items: XeroItem[],
	taxRates: XeroTaxRate[],
	contacts: XeroContact[],
	trackingCategories: XeroTrackingCategory[]
) =>
	orgs.map((org) => ({
		...org,
		accounts: accounts.filter((account) => account.xeroOrgID === org.xeroOrg!.xeroOrgID),
		items: items.filter((item) => item.xeroOrgID === org.xeroOrg!.xeroOrgID),
		taxRates: taxRates.filter((taxRate) => taxRate.xeroOrgID === org.xeroOrg!.xeroOrgID),
		contacts: contacts.filter((contact) => contact.xeroOrgID === org.xeroOrg!.xeroOrgID),
		trackingCategories: trackingCategories.filter((category) => category.xeroOrgID === org.xeroOrg!.xeroOrgID),
	}))

const formatOrgMappings = (orgMappings: StripeOrgAccountXeroOrg[], stripeProducts: StripeProduct[]) =>
	orgMappings.map((orgMapping) => ({
		...orgMapping,
		stripeProductXeroMappings: orgMapping
			.stripeProductXeroMappings!.map((spxm) =>
				!spxm.ignoreInvoices && spxm.xeroAccountID === null && spxm.xeroItemID === null ? { ...spxm, xeroAccountID: 'N/A', xeroItemID: 'N/A' } : spxm
			)
			.concat(
				// add default not in use mapping for any archived products that don't already have mappings
				stripeProducts
					.filter(
						(stripeProduct: StripeProduct) =>
							stripeProduct.isActive === false &&
							!orgMapping.stripeProductXeroMappings!.some((spxm) => spxm.stripeProductID === stripeProduct.stripeProductID)
					)
					.map((stripeProduct: StripeProduct) => defaultNotInUseProductMapping(stripeProduct))
			),
		stripeTaxRateXeroTaxRates: orgMapping.stripeTaxRateXeroTaxRates!.map((strxtr) =>
			strxtr.xeroTaxRateID === null ? { ...strxtr, xeroTaxRateID: 'N/A' } : strxtr
		),
	}))

const defaultNotInUseProductMapping = (product: StripeProduct): StripeProductXeroMapping & { edited: boolean } => ({
	stripeProductID: product.stripeProductID,
	xeroAccountID: 'N/A',
	xeroItemID: 'N/A',
	edited: true,
})

export type {
	OrgSettings,
	HistoryView,
	InvoiceRetryBody,
	EventRetryBody,
	AccountStripeOrgWithData,
	AccountXeroOrgWithData,
	DetailedStripePayout,
	DetailedStripeCharge,
}
export default SubSync
